import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

export function StatusBadge({ value }) {
  const map = {
    Active: "text-[#1B7F3B] border-[#1B7F3B]/30 bg-[#1B7F3B]/5",
    Inactive: "text-[#6B7280] border-gray-300 bg-gray-50",
    Live: "text-[#1B7F3B] border-[#1B7F3B]/30 bg-[#1B7F3B]/5",
    Greenfield: "text-[#D97706] border-[#D97706]/30 bg-[#D97706]/5",
    Yes: "text-[#BF9B30] border-[#BF9B30]/40 bg-[#BF9B30]/5",
  };
  return (
    <span className={`inline-block border px-2 py-0.5 text-[11px] font-medium rounded-sm ${map[value] || "text-[#1E5AA8] border-[#1E5AA8]/30 bg-[#1E5AA8]/5"}`}>
      {value}
    </span>
  );
}

function FieldInput({ field, value, onChange, lookups }) {
  const base = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#BF9B30] focus:border-transparent";
  const options = field.options || (field.optionsFrom ? lookups[field.optionsFrom] || [] : []);
  if (field.type === "select")
    return (
      <select data-testid={`field-${field.key}`} className={base} value={value ?? ""} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">— Select —</option>
        {options.map((o) => (
          <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
        ))}
      </select>
    );
  if (field.type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div data-testid={`field-${field.key}`} className="border border-gray-300 rounded-sm p-2 max-h-32 overflow-y-auto bg-white grid grid-cols-1 sm:grid-cols-2 gap-1">
        {options.map((o) => {
          const v = o.value ?? o;
          return (
            <label key={v} className="flex items-center gap-2 text-xs py-0.5 cursor-pointer">
              <input
                type="checkbox"
                checked={arr.includes(v)}
                onChange={(e) => onChange(e.target.checked ? [...arr, v] : arr.filter((x) => x !== v))}
              />
              {o.label ?? o}
            </label>
          );
        })}
      </div>
    );
  }
  if (field.type === "checkbox")
    return (
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input data-testid={`field-${field.key}`} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        {field.checkLabel || "Yes"}
      </label>
    );
  return (
    <input
      data-testid={`field-${field.key}`}
      className={base}
      type={field.type === "number" ? "number" : field.type === "date" ? "date" : field.type === "password" ? "password" : "text"}
      value={value ?? ""}
      onChange={(e) => onChange(field.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
      placeholder={field.placeholder || ""}
    />
  );
}

export default function MasterCrud({ title, subtitle, endpoint, idField, columns, fields, lookups = {}, canWriteOverride, onDataChange }) {
  const { can } = useAuth();
  const canWrite = canWriteOverride !== undefined ? canWriteOverride : can("masters", "write");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/${endpoint}`)
      .then(({ data }) => { setRows(data); onDataChange && onDataChange(data); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [endpoint]); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
  }, [rows, search]);

  const openCreate = () => {
    const init = {};
    fields.forEach((f) => { init[f.key] = f.default !== undefined ? f.default : (f.type === "multiselect" ? [] : f.type === "checkbox" ? false : ""); });
    setForm(init);
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    const init = {};
    fields.forEach((f) => { init[f.key] = row[f.key] ?? (f.type === "multiselect" ? [] : f.type === "checkbox" ? false : ""); });
    setForm(init);
    setEditing(row);
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/${endpoint}/${editing[idField]}`, form);
        toast.success(`${title} updated`);
      } else {
        await api.post(`/${endpoint}`, form);
        toast.success(`${title} created`);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/${endpoint}/${deleting[idField]}`);
      toast.success("Deleted");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight">{title}</h1>
          {subtitle && <p className="text-sm text-[#6B7280] mt-1">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <input
              data-testid={`${endpoint}-search-input`}
              className="border border-gray-300 rounded-sm pl-8 pr-3 py-2 text-sm bg-white w-44 sm:w-56 focus:outline-none focus:ring-2 focus:ring-[#BF9B30]"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {canWrite && (
            <button
              data-testid={`${endpoint}-add-button`}
              onClick={openCreate}
              className="flex items-center gap-2 bg-[#0E2240] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#1a3358] transition-colors duration-200"
            >
              <Plus size={15} /> Add
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`${endpoint}-table`}>
            <thead>
              <tr className="border-b border-gray-200 bg-[#F5F6F8]">
                {columns.map((c) => (
                  <th key={c.key} className={`px-4 py-3 text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold whitespace-nowrap ${c.align === "right" ? "text-right" : "text-left"}`}>
                    {c.label}
                  </th>
                ))}
                {canWrite && <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-[#6B7280]">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-[#6B7280]">No records</td></tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row[idField]} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors duration-200">
                    {columns.map((c) => (
                      <td key={c.key} className={`px-4 py-3 whitespace-nowrap ${c.align === "right" ? "text-right tabular-nums" : ""}`}>
                        {c.render ? c.render(row, lookups) : (row[c.key] ?? "—")}
                      </td>
                    ))}
                    {canWrite && (
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button data-testid={`${endpoint}-edit-${row[idField]}`} onClick={() => openEdit(row)} className="text-[#1E5AA8] hover:text-[#0E2240] p-1 transition-colors duration-200" aria-label="Edit">
                          <Pencil size={15} />
                        </button>
                        <button data-testid={`${endpoint}-delete-${row[idField]}`} onClick={() => setDeleting(row)} className="text-[#BB162B] hover:text-[#8f1021] p-1 ml-1 transition-colors duration-200" aria-label="Delete">
                          <Trash2 size={15} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-gray-200 text-xs text-[#6B7280] tabular-nums">
          {filtered.length} of {rows.length} records
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white rounded-sm shadow-none border border-gray-200 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#0E2240]">{editing ? `Edit ${title}` : `New ${title}`}</DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">
              All changes are recorded in the audit log.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {fields.map((f) => (
              <div key={f.key} className={f.fullWidth ? "sm:col-span-2" : ""}>
                <label className="block text-xs font-medium text-[#6B7280] uppercase tracking-wider mb-1.5">
                  {f.label}{f.required && <span className="text-[#BB162B]"> *</span>}
                </label>
                <FieldInput field={f} value={form[f.key]} onChange={(v) => setForm((p) => ({ ...p, [f.key]: v }))} lookups={lookups} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors duration-200">Cancel</button>
            <button
              data-testid={`${endpoint}-save-button`}
              onClick={save}
              disabled={saving}
              className="px-4 py-2 text-sm bg-[#0E2240] text-white rounded-sm hover:bg-[#1a3358] transition-colors duration-200 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-white rounded-sm shadow-none border border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-[#0E2240]">Delete record?</AlertDialogTitle>
            <AlertDialogDescription>
              This action is recorded in the audit log and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm shadow-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`${endpoint}-confirm-delete`} onClick={confirmDelete} className="bg-[#BB162B] hover:bg-[#8f1021] rounded-sm shadow-none">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
